import { type FC, memo, type PropsWithChildren } from 'react'
import {
  SplashLoading,
  stencil,
  UnsupportedBrowserNotice,
  useGetNavigationStructureQuery,
  useGetTenantSettingsQuery,
} from 'react-common'
import { useTranslation } from 'react-i18next'
import { Header as UiKitHeader } from '@platform/ui'
import { BreadcrumbsPluginContainer } from '@/features/stencil/BreadcrumbsPluginContainer/BreadcrumbsPluginContainer'
import { useShouldShowSidebar } from '@/features/stencil/HamburgerContainer/useShouldShowSidebar'
import { TenantLogoPlugin } from '@/features/stencil/TenantLogoPlugin/TenantLogoPlugin'
import { useCheckLearnerAccess } from '@/hooks/permission/useCheckLearnerAccess'
import { useHeaderActions } from '@/hooks/useHeaderActions'
import { useLayoutVisibility } from './hooks/useLayoutVisibility'
import styles from './StencilPreset.module.scss'
import { ContentHeaderPreset } from '../ContentHeaderPreset/ContentHeaderPreset'
import { HamburgerContainer } from '../HamburgerContainer/HamburgerContainer'
import { ImpersonationContainer } from '../ImpersonationContainer/ImpersonationContainer'
import { SideBarPluginContainer } from '../SideBarPluginContainer/SideBarPluginContainer'
import { TOCPluginContainer } from '../TOCPluginContainer/TOCPluginContainer'
import { UserMenuPreset } from '../UserMenuPreset/UserMenuPreset'

const { Actions } = UiKitHeader
const {
  Stencil,
  HeaderPlugin,
  ActionsSlotPlugin,
  ContentHeaderPlugin,
  ChatPlugin,
  ChatProvider,
  ThemePlugin,
  SetCustomBrandingColorsPlugin,
  ProctoredProvider,
} = stencil

// INFO: this Stencil component re-renders at every route change, that's why we need to memoize it
export const StencilPreset: FC<PropsWithChildren> = memo(({ children }) => {
  const layoutVisibility = useLayoutVisibility()
  const shouldShowSidebar = useShouldShowSidebar()
  const { t } = useTranslation()

  const { actions, limit } = useHeaderActions()

  const { isLoading } = useGetTenantSettingsQuery()

  const { isLoading: navigationStructureLoading } = useGetNavigationStructureQuery()

  useCheckLearnerAccess(navigationStructureLoading)

  // to avoid flickering on sidebar when loading
  if (isLoading) {
    return <SplashLoading />
  }

  return (
    <Stencil>
      <ProctoredProvider>
        <ChatProvider bottomSheetTitle={t('communicationsHub')}>
          <div className={styles.alertsContainer}>
            <UnsupportedBrowserNotice
              desktopBrowsers={['chrome', 'safari', 'edge']}
              mobileBrowsers={['chrome', 'safari']}
            />
          </div>
          <ThemePlugin />
          <SetCustomBrandingColorsPlugin />

          <HeaderPlugin />
          {!shouldShowSidebar && <ContentHeaderPlugin />}
          <ContentHeaderPreset />
          <TenantLogoPlugin link="/" />
          <BreadcrumbsPluginContainer />
          <HamburgerContainer />
          <TOCPluginContainer />

          {layoutVisibility.sidebar && <SideBarPluginContainer />}

          <ActionsSlotPlugin>
            {actions?.length ? <Actions items={actions} limit={limit as number} /> : null}
          </ActionsSlotPlugin>

          <UserMenuPreset />
          <ImpersonationContainer />

          <ChatPlugin />
          {children}
        </ChatProvider>
      </ProctoredProvider>
    </Stencil>
  )
})
